import React, { useState, memo } from "react";
import classes from "./Accordion.scss";
import { ChevronDownOutlined, ChevronUpOutlined } from "@toddle-edu/ds-icons";
import AnimateHeight from "UIComponents/AnimateHeight";
import classNames from "classnames";
import { IconButton } from "@toddle-edu/ds-web";
import Fields from "../Fields";
import { getPropertyValue } from "Fields/FieldUtils";
import _ from "lodash";
import { onEnterOrSpaceKeyDown } from "Utils";

const Accordion = memo(function Stages(props) {
  const {
    stage_list,
    selectedAcademicYear,
    itemList,
    testId,
    overrideClasses = {},
  } = props;

  const itemKeyList = _.keys(itemList);
  const [collapsedItems, setCollapsedItems] = useState(itemKeyList);

  const toggleCollapsedItems = params => {
    const { ItemKey } = params;
    if (collapsedItems.includes(ItemKey)) {
      setCollapsedItems(_.filter(collapsedItems, item => item !== ItemKey));
    } else {
      setCollapsedItems([...collapsedItems, ItemKey]);
    }
  };

  const accordionHeaderClasses = classNames(
    classes.accordionHeader,
    overrideClasses.accordionHeader
  );

  return (
    <>
      {_.map(itemKeyList, ItemKey => {
        const isExpanded = !collapsedItems.includes(ItemKey);
        const label = _.get(stage_list, `${ItemKey}.label`, null);

        const toggleCollapsedItemsCallBack = () =>
          toggleCollapsedItems({ ItemKey });
        return (
          <div className={classes.accordionContainer} key={ItemKey}>
            <div
              className={accordionHeaderClasses}
              onClick={toggleCollapsedItemsCallBack}
              onKeyDown={e => {
                onEnterOrSpaceKeyDown(e, toggleCollapsedItemsCallBack);
              }}
              data-test-id={`${testId}-${ItemKey}`}
            >
              <div
                className={classNames("text-label-2", classes.accordionLabel)}
              >
                {label}
              </div>
              <div className={classes.arrowIcon}>
                <IconButton
                  _legacy_size="x-small"
                  size="small"
                  _legacy_variant="plain"
                  variant="neutral"
                  type="plain"
                  icon={
                    isExpanded ? (
                      <ChevronUpOutlined variant="subtle" />
                    ) : (
                      <ChevronDownOutlined variant="subtle" />
                    )
                  }
                  testId={`${testId}-${ItemKey}-arrowIcon`}
                />
              </div>
            </div>
            <Steps
              isExpanded={isExpanded}
              {...props}
              stageSteps={itemList[ItemKey].steps}
              selectedAcademicYear={selectedAcademicYear}
            />
          </div>
        );
      })}
    </>
  );
});

const Steps = memo(function Steps(props) {
  const {
    t,
    unitPlanFields,
    step_list,
    field_list,
    fieldListV2,
    isExpanded,
    stageSteps,
    unitPlanDetails,
    selectedAcademicYear,
    isUnitFlowRevamp,
  } = props;

  const stepsContainerClasses = classNames({
    [classes.stepsContainer]: true,
    [classes.stepsContainerRevamp]: isUnitFlowRevamp,
  });

  return (
    <AnimateHeight
      height={isExpanded ? "auto" : 0}
      duration={300}
      onAnimationStart={null}
      onAnimationEnd={null}
      animateOpacity={true}
    >
      <div className={stepsContainerClasses}>
        {_.map(stageSteps, (stepKey, index) => {
          const stepGroupClasses = classNames({
            [classes.stepGroup]: true,
            [classes.stepGroupBorder]: true,
            [classes.stepGroupRevamp]: isUnitFlowRevamp,
          });
          const stepLabel = _.get(
            step_list,
            `${stepKey}.subjectName`,
            _.get(step_list, `${stepKey}.label`, "")
          );
          const fields = _.get(step_list, `${stepKey}.fields`, []);
          const fieldKey = _.head(fields);
          let fieldLabel = "";
          if (_.has(fieldListV2, fieldKey)) {
            const fieldObj = fieldListV2[fieldKey];
            fieldLabel = getPropertyValue({ propertyPath: "label", fieldObj });
          } else {
            const field = field_list[fieldKey] ?? {};
            const { config: { viewLabel, label } = {} } = field;
            fieldLabel = viewLabel || label;
          }

          const showLabel = !_.isEqual(stepLabel, fieldLabel);

          return (
            <div key={stepKey} className={stepGroupClasses}>
              {_.isEmpty(stepLabel) || !showLabel ? null : (
                <div
                  className={classNames(classes.subjectLabel, "text-label-3")}
                  style={
                    index === 0 || isUnitFlowRevamp ? { paddingTop: "0px" } : {}
                  }
                >
                  {stepLabel}
                </div>
              )}
              <Fields
                t={t}
                fields={fields}
                field_list={field_list}
                fieldListV2={fieldListV2}
                unitPlanFields={unitPlanFields}
                unitPlanDetails={unitPlanDetails}
                selectedAcademicYear={selectedAcademicYear}
                isUnitFlowRevamp={isUnitFlowRevamp}
              />
            </div>
          );
        })}
      </div>
    </AnimateHeight>
  );
});

export default Accordion;
